import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { DecisionButtons, SubmissionBadge } from "@/components/ProposalSubmit";

export default function ProposalsPage() {
  const [status, setStatus] = useState("pending");
  const [res, setRes] = useState(null);
  const load = () => api.get("/proposals", { params: status === "all" ? {} : { status } }).then(({ data }) => setRes(data)).catch((err) => toast.error(formatApiError(err)));
  useEffect(() => { setRes(null); load(); }, [status]); // eslint-disable-line
  return (
    <div className="space-y-6" data-testid="proposals-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400">Assessment & proposals</div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight mt-1" style={{ fontFamily: "Manrope" }}>Proposals</h1>
        <div className="text-sm text-slate-500 mt-1">Candidates submitted for Superintendent assessment, and the proposal drafts generated on approval.</div>
      </div>
      <Tabs value={status} onValueChange={setStatus}>
        <div className="max-w-full overflow-x-auto"><TabsList>
          {[["pending", "Awaiting assessment"], ["approved", "Approved"], ["rejected", "Rejected"], ["all", "All"]].map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`proposals-tab-${v}`}>{l}</TabsTrigger>)}
        </TabsList></div>
      </Tabs>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        {!res ? <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 inline" /></div> : (
          <table className="w-full text-sm min-w-[860px]" data-testid="proposals-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>{["Candidate", "Rank", "Vessel / Principal", "Status", "Submitted", ""].map((h) => <th key={h} className="px-5 py-3 text-left font-medium">{h}</th>)}</tr>
            </thead>
            <tbody>
              {res.items.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400" data-testid="proposals-empty">Nothing here.</td></tr>}
              {res.items.map((s) => (
                <tr key={s.id} className="border-t border-slate-100" data-testid={`proposals-row-${s.id}`}>
                  <td className="px-5 py-3.5 font-semibold text-slate-800"><Link to={`/candidates/${s.candidate_id}`} className="hover:text-cyan-700">{s.candidate_name}</Link></td>
                  <td className="px-5 py-3.5 text-slate-700">{s.rank}</td>
                  <td className="px-5 py-3.5 text-slate-700">{s.vessel_name} / {s.principal_name}</td>
                  <td className="px-5 py-3.5"><SubmissionBadge s={s} />{s.proposal_status && <div className="text-xs text-slate-500 mt-1" data-testid={`proposals-sent-${s.id}`}>{s.proposal_status === "sent" ? `Emailed ${new Date(s.last_sent_at).toLocaleDateString()}` : "Draft not sent"}</div>}</td>
                  <td className="px-5 py-3.5 text-slate-500 text-xs">{new Date(s.submitted_at).toLocaleString()}<div>{s.submitted_by_name}</div></td>
                  <td className="px-5 py-3.5 text-right">
                    {s.status === "pending" && res.can_decide && <DecisionButtons s={s} onDone={load} />}
                    {s.proposal_id && <Button asChild size="sm" variant="outline" data-testid={`proposals-open-draft-${s.id}`}><Link to={`/proposals/${s.proposal_id}`}>Open draft</Link></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
